import { useApi, type ApiState } from "./useApi.js";
import type { Role } from "../roles.js";

/**
 * E8.11 (NX-2640) — the connector service: the ONLY place the Connectors screen learns how to
 * reach the API. The screen and its sub-components take a `ConnectorService` and never call
 * `apiGet`/`fetch` themselves, so a sub-component cannot grow an inline fetch (AC5, E8.5 SOLID).
 *
 * The shapes below mirror apps/api/src/connector-mgmt.ts's `ConnectorStateView` rather than being
 * redeclared loosely: `capability` is the adapter's own manifest (`provides` = what the source CAN
 * observe, `cannot` = what it CANNOT — an honest N/A, never scored as a zero), `coverage` is the
 * observed windows, `syncState` is the ingest cursor. `completeness` and `lagSeconds` are optional
 * on the API side "only when derivable", so they stay optional here — a missing one renders as
 * not-derivable, never as 0.
 */

export type EntityType = string;

export interface ConnectorCapability {
  readonly sourceType: string;
  readonly provides: readonly EntityType[];
  readonly cannot: readonly EntityType[];
  readonly webhook: boolean;
  readonly backfill: boolean;
}

export interface CoverageWindow {
  readonly entityType: EntityType;
  readonly from: string;
  readonly through: string;
  readonly completeness?: number;
  readonly lagSeconds?: number;
}

export interface ConnectorSyncState {
  readonly cursorValue: string | null;
  readonly updatedAt: string | null;
}

export interface ConnectorState {
  readonly sourceType: string;
  readonly capability: ConnectorCapability;
  readonly coverage: readonly CoverageWindow[];
  readonly syncState: ConnectorSyncState;
}

export interface ProjectConnectors {
  readonly projectId: string;
  readonly connectors: readonly ConnectorState[];
}

/** The minimum the Connectors screen needs from the portfolio to offer a project selector (AC3). */
export interface ConnectorProjectOption {
  readonly id: string;
  readonly name: string;
}

/**
 * NX-2646 restricts both connector endpoints to admin server-side. The client mirrors that with
 * the same constant rather than a second opinion about who may read connector internals, and
 * TopBar's NAV entry (`roles: ["admin"]`) is the third expression of the same boundary.
 */
export const CONNECTOR_ROLES: readonly Role[] = ["admin"];

export function canReadConnectors(role: Role): boolean {
  return CONNECTOR_ROLES.includes(role);
}

/**
 * The service contract. Each member is a hook, so an implementation composes `useApi` and the
 * screen stays a plain consumer.
 *
 * `useProjectConnectors` always takes a concrete id — never null. The "no project selected" case
 * is handled by MOUNTING the per-project panel only once a project is chosen, rather than by
 * calling the hook with a sentinel path: `useApi` has no skip mode, and passing an empty path
 * would issue a real request to /api/v1 and park the caller in `loading` forever. Conditional
 * mounting keeps every hook call unconditional within its own component.
 */
export interface ConnectorService {
  useConnectors(): ApiState<ConnectorState[]>;
  useProjects(): ApiState<ConnectorProjectOption[]>;
  useProjectConnectors(projectId: string): ApiState<ProjectConnectors>;
}

/** The live service over /api/v1 — the only implementation the app itself ever uses. */
export const liveConnectorService: ConnectorService = {
  useConnectors: () => useApi<ConnectorState[]>("/connectors"),
  useProjects: () => useApi<ConnectorProjectOption[]>("/portfolio"),
  useProjectConnectors: (projectId) =>
    useApi<ProjectConnectors>(`/projects/${projectId}/connectors`),
};
